/**
 * Keeps the grid's row grouping in step with the chart's dimension selection.
 *
 * The integration doesn't aggregate — it reads rows at depth `dimensions.length - 1`, which
 * on an ungrouped grid is the flat leaf rows, so charting Type over 30k records draws 30k
 * arcs. Its own fix is gated on there already being a grouping or more than one dimension,
 * so a single dimension on an ungrouped grid falls straight through. This closes that gap.
 *
 * After a MUI bump, `npm run test:upstream` says whether the gap is still there.
 */

import { useEffect } from 'react';
import type { useGridApiRef } from '@mui/x-data-grid-premium';

import { getRequiredGrouping, isGroupingSufficient } from '../utils/chartState.ts';

type GridApiRef = ReturnType<typeof useGridApiRef>;

export function useChartDimensionGrouping(
    apiRef: GridApiRef,
    chartId: string,
    maxDimensions: number | undefined
): void {
    useEffect(() => {
        if (!apiRef.current) return undefined;

        let disposed = false;
        let scheduled = false;

        const reconcile = () => {
            scheduled = false;
            const api = apiRef.current;
            if (disposed || !api) return;

            const desired = getRequiredGrouping(
                api.state.chartsIntegration?.charts?.[chartId]?.dimensions,
                maxDimensions
            );

            // An empty selection never ungroups. Dismissing a chart clears both sections,
            // and dropping 30k rows back into a flat grid is the cost we're here to avoid.
            if (desired.length === 0) return;

            const active = api.state.rowGrouping.model;
            if (isGroupingSufficient(desired, active)) return;

            // The same pair of writes the integration makes: group by the dimensions, hide
            // those columns, and give back whatever the previous grouping had hidden.
            api.setRowGroupingModel(desired);
            api.setColumnVisibilityModel({
                ...api.state.columns.columnVisibilityModel,
                ...Object.fromEntries(active.map((field) => [field, true])),
                ...Object.fromEntries(desired.map((field) => [field, false])),
            });
        };

        const schedule = () => {
            if (scheduled) return;
            scheduled = true;
            // A microtask, not a timeout: the grouping has to land before the chart
            // initializes, and MUI's own scheduling is debounced onto the task queue.
            queueMicrotask(reconcile);
        };

        // `stateChange` fires for every grid mutation, so scheduling keeps this cheap — the
        // reconcile itself runs once a tick.
        const unsubscribe = apiRef.current.subscribeEvent('stateChange', schedule);

        // Catch a selection already in place: a restored report, or a chart type change that
        // narrowed `maxDimensions`.
        schedule();

        return () => {
            disposed = true;
            unsubscribe();
        };
    }, [apiRef, chartId, maxDimensions]);
}
